import { Form, Input, InputNumber, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import {
  getLabelsAsValues,
  getValuesFromLabels,
  SelectOptionsType,
} from "../../../utils/select-helpers";
import { getLocationsUnPooled } from "../../../api/pool/getLocationsUnpooled";
import { getSipNumbersUnPooled } from "../../../api/pool/getSipNumbersUnPooled";
import { postPools } from "../../../api/pool/postPools";
import { TransformedDataType } from "../../../api/pool/getPoolsData";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
  allPools: TransformedDataType[];
}

interface State {
  isModalVisible: boolean;
  unpooledLocations?: SelectOptionsType[];
  threshold: number;
  submitError?: string;
}

const AddNewPoolFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
  allPools,
}) => {
  const [form] = Form.useForm();

  const [state, setState] = useState<State>({
    isModalVisible: false,
    unpooledLocations: undefined,
    threshold: 10,
  });

  const { isModalVisible, unpooledLocations, submitError } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk formFields: %o", formFields);
        const payload = {
          name: formFields.poolName,
          locationNames: getValuesFromLabels(
            formFields.locations,
            unpooledLocations
          ),
          isActive: true,
          threshold: formFields.threshold,
        };
        console.log("postPools() payload: %o", payload);
        postPools(payload).then((postResponse) => {
          console.log("submitResponse:%o", postResponse);
          if (postResponse?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: postResponse.error,
            }));
          } else {
            form.resetFields();
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            handleClose(true);
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setState((prevState) => ({
        ...prevState,
        isModalVisible: isVisible,
        submitError: "",
      }));

      if (!isVisible) return;

      getLocationsUnPooled().then((response) => {
        if (response.data) {
          setState((prevState) => ({
            ...prevState,
            unpooledLocations: response.data,
          }));
        }
      });

      getSipNumbersUnPooled().then((response) => {
        if (response.data) {
          setState((prevState) => ({
            ...prevState,
            unpooledSipNumbers: response.data,
          }));
        }
      });
    }
    // eslint-disable-next-line
  }, [isVisible, isModalVisible]);

  useEffect(() => {}, []);

  return (
    <Modal
      title="Add New Pool"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <div className="my-4" />
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          label="Pool Name:"
          name="poolName"
          rules={[
            { required: true, message: "This field is required" },
            { max: 100, message: "Pool name cannot exceed 100 characters." },
            {
              validator: (_, value) => {
                if (
                  value &&
                  allPools?.some(
                    (pool) =>
                      pool?.name?.toLowerCase().trim() ===
                      value.toLowerCase().trim()
                  )
                ) {
                  return Promise.reject("Pool name already exists");
                }
                return Promise.resolve();
              },
            },
          ]}
          labelCol={{ span: 24 }} // Makes label take full width
          wrapperCol={{ span: 24 }} // Makes input take full width
        >
          <Input className="h-8" placeholder="Enter a pool name" />
        </Form.Item>

        <Form.Item
          name="locations"
          label="Locations:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            className="min-h-8"
            placeholder="Select locations"
            mode="multiple"
            onChange={(values: string[]) => {
              const sortedValues = [...values].sort();
              form.setFieldsValue({ locations: sortedValues });
            }}
            allowClear
            options={getLabelsAsValues(unpooledLocations)}
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().startsWith(inputValue.toUpperCase())
            }
          ></Select>
        </Form.Item>

        <Form.Item
          name="threshold"
          initialValue={10}
          label="Threshold Value:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            { required: true, message: "This field is required" },
            {
              type: "number",
              min: 1,
              max: 50,
              message: "Threshold value must be a number between 1 and 50",
            },
          ]}
        >
          <InputNumber
            className="h-8 w-full"
            placeholder="Enter a number between 0 and 50"
          />
        </Form.Item>
      </Form>

      {submitError && <div className="text-red-500">{submitError}</div>}
    </Modal>
  );
};

export default AddNewPoolFormModal;
